/**
 * Cliente HTTP para documentos-fonte de um caso (E8.F2 backend → E8.F3 UI).
 *
 * Espelha apps/api/types/case_source_documents.py e
 * apps/api/runtime/case_source_documents.py. Cobre:
 * - uploadDocument: POST multipart /casos/:case_id/documents?kind=...
 * - listDocuments: GET /casos/:case_id/documents (filtro opcional por kind)
 * - getDocument: GET /documents/:doc_id
 * - reextractDocument: POST /documents/:doc_id/extract
 * - deleteDocument: DELETE /documents/:doc_id
 * - getDocumentPageUrl: helper de path para o canvas Fabric.js (E8.F4)
 */
import axios, { AxiosError, type AxiosInstance } from 'axios';
import type { SeparacaoApuradaData } from './exame_eletronico_arquivo_unico';
import { nomeDoAnexo, type ArquivoBaixado } from './http_download';

/**
 * `protocolo` (E84.F1, ADR-0078 §2.8): o comprovante de formalização reconhecido
 * pelo conteúdo no passo 2 de um caso eletrônico. Fica na custódia do caso e
 * aparece na lista; NÃO é peça — nenhuma contagem de peças o inclui.
 * `origem` (E97, ADR-0082): o arquivo único recebido; só volta na resposta do envio,
 * a listagem o omite (os documentos contidos são os documentos).
 */
export type SourceDocumentKind =
  | 'standard'
  | 'contested'
  | 'protocolo'
  | 'origem'
  // E107.F1 (ADR-0087): o `.zip` recebido (a listagem o omite, como a origem) e a evidência do
  // banco — manifesto e carimbo do tempo —, listada sem prévia e nunca peça.
  | 'pacote'
  | 'evidencia';

/**
 * O que o cliente pode PEDIR no upload (E84.F1.C01): `protocolo` é decisão do
 * servidor, pelo conteúdo — pedi-lo é 422.
 */
export type SourceDocumentKindEnviado = 'standard' | 'contested';

/**
 * Espelha `ConteudoAtivo` de `apps/api/types/conteudo_ativo.py` (E146.F2, ADR-0100 §2.7): no PDF, o
 * que veio e não foi executado; no DOCX, o que saiu da cópia examinada.
 */
export type ConteudoAtivo =
  | 'javascript'
  | 'acao_automatica'
  | 'abertura_de_programa'
  | 'link_externo'
  | 'envio_de_formulario'
  | 'arquivo_embutido'
  | 'formulario_xfa'
  | 'macro'
  | 'vinculo_externo'
  | 'objeto_embutido';

export type ExtractionStatus = 'pending' | 'running' | 'completed' | 'failed' | 'skipped';

export type ExtractionMethod = 'llm_vision' | 'manual' | 'mixed';

/**
 * Metadados forenses propostos a partir de uma CCB (E23.F2/F3). Espelha
 * apps/api/types/ccb_metadata.py::CcbMetadata. Proposta para pré-preenchimento
 * — confirmação humana obrigatória antes de virar dado do caso (LGPD).
 */
export interface CcbMetadataData {
  sbr_identification: string | null;
  cpf: string | null;
  author: string | null;
  process_number: string | null;
  defendant: string | null;
  contract: string | null;
  /**
   * De onde os campos vieram. `sem_texto` (E82.F1) diz que o arquivo foi aceito
   * e não tinha camada de texto — foto ou escaneado. É diferente dos seis
   * campos virem nulos depois de uma leitura bem-sucedida, e a tela de
   * importação tem coisas diferentes a dizer nos dois casos.
   */
  extraction_source: 'regex' | 'vision' | 'sem_texto';
}

export interface SourceDocumentRecordData {
  id: string;
  case_id: string;
  usuario_id: string;
  kind: SourceDocumentKind;
  // E50.F3/F4: número da CCB declarada no slot do portal (null no fluxo do perito).
  ccb_number?: string | null;
  // E90.F6: o nome dado pelo perito ao documento; null = sem nome editado.
  nome_do_documento?: string | null;
  original_filename: string;
  blob_path: string;
  mime_type: string;
  size_bytes: number;
  page_count: number | null;
  /** E58.F1 (ADR-0067): hash dos bytes em claro na ingestão; nulo só em legado. */
  content_sha256?: string | null;
  has_embedded_images: boolean;
  extraction_status: ExtractionStatus;
  extraction_method: ExtractionMethod | null;
  extraction_error: string | null;
  extraction_started_at: string | null;
  extraction_completed_at: string | null;
  created_at: string;
  deleted_at?: string | null;
  extracted_metadata?: CcbMetadataData | null;
  /** E97.F4.T03: só no arquivo único recebido — o que a separação apurou dele. */
  separacao?: SeparacaoApuradaData | null;
  /** E101.F1: a leitura por imagem do arquivo sem camada de texto; nulo = arquivo com texto. */
  leitura_por_imagem?: EstadoDaLeituraPorImagem | null;
  /** E101.F1: por que a leitura por imagem falhou — só em `falhou`. */
  leitura_por_imagem_motivo?: MotivoDaFalhaDaLeitura | null;
  leitura_iniciada_em?: string | null;
  leitura_concluida_em?: string | null;
  /** E111.F1 — só na resposta do envio: os bytes são os de um documento que já estava no caso. */
  copia_identica?: boolean;
  /** E107.F1: o pacote `.zip` de onde o arquivo saiu; nulo = chegou sozinho. */
  pacote_document_id?: string | null;
  /** E146.F2: o conteúdo ativo do arquivo recebido; vazio = nenhum. */
  conteudo_ativo?: ConteudoAtivo[];
}

/** Espelha `MotivoDoIgnorado` de `apps/api/types/pacote_do_banco.py` (E107.F1). */
export type MotivoDoIgnorado = 'formato_nao_aceito' | 'profundidade_excedida' | 'arquivo_recusado';

/** Espelha `PacoteRecebido`: o que entrou no caso e o que ficou de fora do pacote. */
export interface PacoteRecebidoData {
  pacote_document_id: string;
  documentos: SourceDocumentRecordData[];
  ignorados: { caminho: string; motivo: MotivoDoIgnorado }[];
  /** E111.F1 — o que já estava no caso com os mesmos bytes e não entrou de novo. */
  copias_identicas?: { caminho: string; documento_id: string }[];
}

/** O pacote vai à rota própria; o resto, ao upload avulso. */
export function ehPacote(file: File): boolean {
  return (
    file.name.toLowerCase().endsWith('.zip') ||
    file.type === 'application/zip' ||
    file.type === 'application/x-zip-compressed'
  );
}

/** Espelha `EstadoDaLeitura` de `apps/api/types/leitura_por_imagem.py` (E101.F1). */
export type EstadoDaLeituraPorImagem = 'pendente' | 'lendo' | 'concluida' | 'falhou';

/** Espelha `MotivoDaFalhaDaLeitura` de `apps/api/types/leitura_por_imagem.py` (E101.F1). */
export type MotivoDaFalhaDaLeitura =
  | 'acima_do_teto'
  | 'arquivo_ilegivel'
  | 'nada_lido'
  | 'cadeia_interrompida';

/**
 * O que o envio de um arquivo único apurou (E97.F4.T03): quantos documentos criou, ou os
 * motivos por que não separou. `null` quando o arquivo enviado não era arquivo único.
 */
export function separacaoDoEnvio(
  record: SourceDocumentRecordData,
): { documentos: number } | { motivos: string } | null {
  const separacao = record.kind === 'origem' ? record.separacao : null;
  if (separacao === null || separacao === undefined) return null;
  if (separacao.situacao === 'conferida') return { documentos: separacao.documentos.length };
  return { motivos: separacao.motivos.map((motivo) => motivo.detalhe).join('; ') };
}

export interface DocumentUploadPayload {
  file: File;
  kind: SourceDocumentKindEnviado;
  /** E50.F4: nº da CCB do slot do portal (só com kind='contested'). */
  ccbNumber?: string;
  onProgress?: (percent: number) => void;
}

// Erros tipados -----------------------------------------------------------

export type DocumentErrorReason =
  | 'case_not_found'
  | 'document_not_found'
  | 'unsupported_mime'
  | 'file_too_large'
  | 'file_empty'
  | 'page_number_invalid'
  | 'page_not_found'
  | 'render_failed'
  | 'unreadable'
  // E50.F4: nº de CCB repetido entre documentos vivos do caso (409 do backend).
  | 'ccb_duplicate'
  | 'validation'
  | 'network'
  // E111.F1 (GUS-122): o tempo de espera do envio acabou; o servidor pode ter recebido.
  | 'timeout'
  | 'rate_limited'
  // E107.F1: o pacote inteiro recusado.
  | 'pacote_ilegivel'
  | 'pacote_cifrado'
  | 'pacote_grande_demais'
  | 'pacote_vazio'
  // E133.F2: o recebimento do lote não é mais do caso — a retentativa abre outro.
  | 'recebimento_not_found'
  // E134.F1: o arquivo guardado não reproduz o hash do recebimento e não desce (409).
  | 'original_divergente'
  // E135.F1: o recebimento já foi selado com o manifesto — a retentativa abre outro.
  | 'recebimento_concluido'
  // E205.F4.T03: o arquivo está fora do recorte do acervo desta conta (403).
  | 'fora_do_recorte'
  | 'unknown';

export class DocumentError extends Error {
  public readonly reason: DocumentErrorReason;

  constructor(reason: DocumentErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'DocumentError';
  }
}

const HTTP_BAD_REQUEST = 400;
const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_TOO_MANY_REQUESTS = 429;
// E146.F1 (ADR-0100 §2.3): o corpo acima do teto é cortado antes da rota (`CORPO_ACIMA_DO_LIMITE`).
const HTTP_PAYLOAD_TOO_LARGE = 413;
const HTTP_UNPROCESSABLE = 422;

/**
 * O navegador desistiu de esperar (E111.F1, GUS-122). No PAN o servidor levou 22 e 26 s para
 * receber o pacote (reconhecimento de texto de dois comprovantes e um PDF de 7,7 MB sem texto) e
 * gravou os documentos nas duas vezes, mas a tela dizia "sem conexão" aos 15 s. Tempo esgotado
 * não é falta de conexão: o envio pode ter chegado.
 */
function isTimeout(err: AxiosError): boolean {
  return err.code === 'ETIMEDOUT' || (err.code === 'ECONNABORTED' && /timeout/i.test(err.message));
}

function isNetworkError(err: AxiosError): boolean {
  return err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED';
}

function detailString(err: AxiosError): string {
  const data = err.response?.data as { detail?: unknown } | undefined;
  return typeof data?.detail === 'string' ? data.detail : '';
}

const NOT_FOUND_BY_DETAIL: Record<string, DocumentErrorReason> = {
  CASE_NOT_FOUND: 'case_not_found',
  PAGE_NOT_FOUND: 'page_not_found',
  RECEBIMENTO_NOT_FOUND: 'recebimento_not_found',
};

const BAD_REQUEST_BY_DETAIL: Record<string, DocumentErrorReason> = {
  UNSUPPORTED_MIME: 'unsupported_mime',
  FILE_TOO_LARGE: 'file_too_large',
  FILE_EMPTY: 'file_empty',
  PAGE_NUMBER_INVALID: 'page_number_invalid',
  // E23.F3: CCB .docx ilegível no preview (F-E23.F3-03).
  DOCUMENT_UNREADABLE: 'unreadable',
  PACOTE_ILEGIVEL: 'pacote_ilegivel',
  PACOTE_CIFRADO: 'pacote_cifrado',
  PACOTE_GRANDE_DEMAIS: 'pacote_grande_demais',
  PACOTE_VAZIO: 'pacote_vazio',
};

const HTTP_CONFLICT = 409;

/** Os 409 que a tela distingue; o resto segue sem motivo próprio. */
const CONFLICT_BY_DETAIL: Record<string, DocumentErrorReason> = {
  CCB_NUMBER_ALREADY_IN_CASE: 'ccb_duplicate',
  // E134.F1: o arquivo guardado não reproduz o hash do recebimento e não desce.
  ORIGINAL_DIVERGENTE: 'original_divergente',
  RECEBIMENTO_CONCLUIDO: 'recebimento_concluido',
};

/** Os status que dizem o motivo sozinhos, sem olhar o `detail`. */
const REASON_BY_STATUS: Partial<Record<number, DocumentErrorReason>> = {
  [HTTP_TOO_MANY_REQUESTS]: 'rate_limited',
  [HTTP_PAYLOAD_TOO_LARGE]: 'file_too_large',
};

/** E205.F4.T03: o único 403 com motivo próprio é o recorte do acervo (`acervo_download`). */
const FORBIDDEN_BY_DETAIL: Record<string, DocumentErrorReason> = {
  ACERVO_FORA_DO_RECORTE: 'fora_do_recorte',
};

/** Por status: os `detail` que a tela distingue e o motivo de quando o `detail` não diz. */
const BY_STATUS_AND_DETAIL: Partial<
  Record<
    number,
    { porDetalhe: Record<string, DocumentErrorReason>; padrao: DocumentErrorReason | null }
  >
> = {
  [HTTP_NOT_FOUND]: { porDetalhe: NOT_FOUND_BY_DETAIL, padrao: 'document_not_found' },
  [HTTP_BAD_REQUEST]: { porDetalhe: BAD_REQUEST_BY_DETAIL, padrao: 'validation' },
  [HTTP_FORBIDDEN]: { porDetalhe: FORBIDDEN_BY_DETAIL, padrao: null },
  [HTTP_CONFLICT]: { porDetalhe: CONFLICT_BY_DETAIL, padrao: null },
};

function reasonFromHttpStatus(
  httpStatus: number | undefined,
  detail: string,
): DocumentErrorReason | null {
  if (httpStatus === HTTP_UNPROCESSABLE) {
    return detail.startsWith('RENDER_FAILED') ? 'render_failed' : 'validation';
  }
  const regra = BY_STATUS_AND_DETAIL[httpStatus ?? 0];
  if (regra === undefined) return null;
  return regra.porDetalhe[detail] ?? regra.padrao;
}

/**
 * E205.F4.T01: o download pede `blob`, e o corpo da recusa chega como `Blob`, não como JSON — o
 * `detail` (`ORIGINAL_DIVERGENTE`, `ACERVO_FORA_DO_RECORTE`) se perdia e a tela dizia "Não foi
 * possível abrir o documento" para as três causas. Aqui o corpo JSON é lido antes de classificar.
 */
export async function comCorpoLido(err: unknown): Promise<unknown> {
  if (!axios.isAxiosError(err)) return err;
  const resposta = err.response;
  if (resposta === undefined || !(resposta.data instanceof Blob)) return err;
  try {
    resposta.data = JSON.parse(await resposta.data.text()) as unknown;
  } catch {
    // Corpo que não é JSON: segue o status.
  }
  return err;
}

export function classifyDocumentError(err: unknown): DocumentError {
  if (!axios.isAxiosError(err)) return new DocumentError('unknown');
  const axiosErr = err as AxiosError;
  const detail = detailString(axiosErr);
  const httpStatus = axiosErr.response?.status;
  const reason = REASON_BY_STATUS[httpStatus ?? 0] ?? reasonFromHttpStatus(httpStatus, detail);
  if (reason !== null) {
    return new DocumentError(reason, reason === 'render_failed' ? detail : undefined);
  }
  if (isTimeout(axiosErr)) return new DocumentError('timeout');
  if (isNetworkError(axiosErr)) return new DocumentError('network');
  return new DocumentError('unknown');
}

// HTTP --------------------------------------------------------------------

/**
 * Extrai metadados de uma CCB `.docx` para pré-preenchimento, sem persistir
 * (E23.F3). Stateless: não há caso ainda. POST multipart /ccb/metadata-preview.
 */
export async function previewCcbMetadata(
  client: AxiosInstance,
  file: File,
): Promise<CcbMetadataData> {
  const form = new FormData();
  form.append('file', file);
  try {
    const { data } = await client.post<CcbMetadataData>('/ccb/metadata-preview', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (err) {
    throw classifyDocumentError(err);
  }
}

/**
 * Tempo de espera do envio, e não os 15 s do cliente (E111.F1, GUS-122). O servidor lê o arquivo
 * na mesma requisição — separa o arquivo único, reconhece o comprovante e, no pacote, faz isso
 * com cada arquivo dele — e 50 MB pela rede também levam tempo.
 */
export const UPLOAD_TIMEOUT_MS = 10 * 60_000;

export async function uploadDocument(
  client: AxiosInstance,
  caseId: string,
  payload: DocumentUploadPayload,
): Promise<SourceDocumentRecordData> {
  const form = new FormData();
  form.append('file', payload.file);
  try {
    const { data } = await client.post<SourceDocumentRecordData>(
      `/casos/${caseId}/documents`,
      form,
      {
        params:
          payload.ccbNumber !== undefined && payload.ccbNumber !== ''
            ? { kind: payload.kind, ccb_number: payload.ccbNumber }
            : { kind: payload.kind },
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: UPLOAD_TIMEOUT_MS,
        onUploadProgress: (evt) => {
          if (payload.onProgress && evt.total != null && evt.total > 0) {
            payload.onProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        },
      },
    );
    return data;
  } catch (err) {
    throw classifyDocumentError(err);
  }
}

/**
 * E107.F1 (ADR-0087): o pacote `.zip` do banco, aberto no servidor. Cada arquivo segue o
 * caminho do upload avulso; a resposta traz o que entrou e o que ficou de fora.
 */
export async function uploadPacote(
  client: AxiosInstance,
  caseId: string,
  payload: Pick<DocumentUploadPayload, 'file' | 'kind' | 'onProgress'>,
): Promise<PacoteRecebidoData> {
  const form = new FormData();
  form.append('file', payload.file);
  try {
    const { data } = await client.post<PacoteRecebidoData>(
      `/casos/${caseId}/documents/pacote`,
      form,
      {
        params: { kind: payload.kind },
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: UPLOAD_TIMEOUT_MS,
        onUploadProgress: (evt) => {
          if (payload.onProgress && evt.total != null && evt.total > 0) {
            payload.onProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        },
      },
    );
    return data;
  } catch (err) {
    throw classifyDocumentError(err);
  }
}

export async function listDocuments(
  client: AxiosInstance,
  caseId: string,
  kind?: SourceDocumentKind,
): Promise<SourceDocumentRecordData[]> {
  try {
    const { data } = await client.get<SourceDocumentRecordData[]>(
      `/casos/${caseId}/documents`,
      kind ? { params: { kind } } : undefined,
    );
    return data;
  } catch (err) {
    throw classifyDocumentError(err);
  }
}

/**
 * Baixa o arquivo ORIGINAL do documento — o que foi enviado ao caso (E65.F6).
 *
 * Não é a imagem da página nem o recorte da assinatura, que são derivados. O
 * `case_id` vai na rota porque o servidor confere que o documento é daquele
 * caso: a RLS isola tenants, não dois casos do mesmo escritório.
 *
 * Devolve o nome do `Content-Disposition` junto com os bytes (C04): é o
 * servidor quem sanea o nome e corrige a extensão pelo mime da ingestão, e
 * usar o `original_filename` da listagem desfaria isso na hora de salvar.
 */
export async function baixarArquivoOriginal(
  client: AxiosInstance,
  caseId: string,
  docId: string,
  nomeSugerido: string,
): Promise<ArquivoBaixado> {
  try {
    const resposta = await client.get<Blob>(`/casos/${caseId}/documents/${docId}/arquivo`, {
      responseType: 'blob',
    });
    return {
      blob: resposta.data,
      filename: nomeDoAnexo(resposta.headers['content-disposition'], nomeSugerido),
    };
  } catch (err) {
    throw classifyDocumentError(await comCorpoLido(err));
  }
}

export async function getDocument(
  client: AxiosInstance,
  docId: string,
): Promise<SourceDocumentRecordData> {
  try {
    const { data } = await client.get<SourceDocumentRecordData>(`/documents/${docId}`);
    return data;
  } catch (err) {
    throw classifyDocumentError(err);
  }
}

export async function reextractDocument(
  client: AxiosInstance,
  docId: string,
): Promise<SourceDocumentRecordData> {
  try {
    const { data } = await client.post<SourceDocumentRecordData>(`/documents/${docId}/extract`);
    return data;
  } catch (err) {
    throw classifyDocumentError(err);
  }
}

export async function deleteDocument(client: AxiosInstance, docId: string): Promise<void> {
  try {
    await client.delete(`/documents/${docId}`);
  } catch (err) {
    throw classifyDocumentError(err);
  }
}

/**
 * Grava o nome do documento (E90.F6, GUS-14); `null` apaga e a listagem volta
 * ao tipo identificado ou ao arquivo. O servidor apara espaços e recusa vazio
 * e acima de 120 caracteres (422).
 */
export async function renomearDocumento(
  client: AxiosInstance,
  docId: string,
  nome: string | null,
): Promise<SourceDocumentRecordData> {
  try {
    const { data } = await client.patch<SourceDocumentRecordData>(`/documents/${docId}/nome`, {
      nome_do_documento: nome,
    });
    return data;
  } catch (err) {
    throw classifyDocumentError(err);
  }
}

export function getDocumentPageUrl(docId: string, pageNumber: number): string {
  return `/documents/${docId}/pages/${pageNumber}`;
}

// Helpers de UI -----------------------------------------------------------

/**
 * Estados terminais: o polling pode parar quando o documento atinge um deles.
 */
const TERMINAL_STATUSES: ReadonlySet<ExtractionStatus> = new Set([
  'completed',
  'failed',
  'skipped',
]);

export function isExtractionTerminal(status: ExtractionStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

/**
 * Classifica `extraction_error` para mapear a string de UI correta. // ux-neutrality:allow doc-comment
 * Reconhece o sinal de rate limit propagado pelo backend (E8.F2.T05). // ux-neutrality:allow doc-comment
 */
export function isRateLimitError(extractionError: string | null): boolean {
  if (typeof extractionError !== 'string') return false;
  const normalized = extractionError.toLowerCase();
  return normalized.includes('rate') || normalized.includes('429');
}

/** E70.F2: recusa da CONTA do provedor — a causa está fora do produto. */
export type ProviderFailure = 'provider_credit' | 'provider_auth';

/**
 * Reconhece as chaves estáveis que o backend grava tanto em
 * `extraction_error` (`provider_credit`) quanto em `error_reason` da análise
 * (`assistant: provider_credit`). Qualquer outro valor → `null`.
 */
export function classifyProviderFailure(reason: string | null | undefined): ProviderFailure | null {
  if (typeof reason !== 'string') return null;
  if (reason.includes('provider_credit')) return 'provider_credit';
  if (reason.includes('provider_auth')) return 'provider_auth';
  return null;
}
